import React, { useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { login, forgotPassword } from '../api';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';

// Helper object to control the favicon spinner animation
const faviconSpinner = {
  originalHref: null,
  animationFrameId: null,
  ctx: null,
  canvas: null,
  progress: 0,

  getFavicon: function() {
    return document.querySelector('link[rel="icon"]');
  },

  start: function() {
    const favicon = this.getFavicon();
    if (!favicon) return;

    this.originalHref = favicon.href;
    this.canvas = document.createElement('canvas');
    this.canvas.width = 32;
    this.canvas.height = 32;
    this.ctx = this.canvas.getContext('2d');

    const animate = () => {
      this.progress = (this.progress + 0.05) % (2 * Math.PI);
      this.draw(this.progress);
      favicon.href = this.canvas.toDataURL('image/png');
      this.animationFrameId = requestAnimationFrame(animate);
    };

    animate();
  },

  draw: function(progress) {
    this.ctx.clearRect(0, 0, 32, 32);
    this.ctx.beginPath();
    this.ctx.arc(16, 16, 12, 0, 2 * Math.PI);
    this.ctx.strokeStyle = '#f0f0f0';
    this.ctx.lineWidth = 4;
    this.ctx.stroke();

    this.ctx.beginPath();
    this.ctx.arc(16, 16, 12, progress, progress + Math.PI / 2);
    this.ctx.strokeStyle = '#0d6efd'; // Bootstrap primary color
    this.ctx.lineWidth = 4;
    this.ctx.stroke();
  },

  stop: function() {
    cancelAnimationFrame(this.animationFrameId);
    const favicon = this.getFavicon();
    if (favicon && this.originalHref) {
      favicon.href = this.originalHref;
    }
  }
};

const openPolicyLink = (e, filename) => {
  e.preventDefault();
  const url = `https://transportesemilia.ajisolutionsspa.cl/${filename}`;
  const isCapacitor = window.Capacitor || (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.bridge);
  if (isCapacitor) {
    window.open(url, '_system');
  } else {
    window.open(url, '_blank');
  }
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate(); // eslint-disable-line no-unused-vars

  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isForgotLoading, setIsForgotLoading] = useState(false);

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setIsForgotLoading(true);
    try {
      await forgotPassword(forgotEmail);
      toast.success('Se ha enviado un enlace de restablecimiento a tu correo electrónico.');
      setShowForgotModal(false);
      setForgotEmail('');
    } catch (err) {
      console.error(err);
    } finally {
      setIsForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    faviconSpinner.start();
    try {
      await login(email, password);
      // No navegamos manualmente, dejamos que RoleBasedRedirect en App.js haga su trabajo
    } catch (err) {
      console.error("Login error:", err);
      if (err.response && err.response.status === 401) {
        setError('Email o clave incorrecta. Por favor, revisa tus datos.');
      } else {
        setError('Ocurrió un error. Inténtalo de nuevo más tarde.');
      }
      faviconSpinner.stop();
      setIsLoading(false);
    }
  };

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card shadow-sm border-0">
            <div className="card-body p-4">
              <div className="text-center mb-4">
                <img src="/EmiGo.png" alt="EmiGo Logo" style={{ maxWidth: '120px' }} />
                <h3 className="card-title mt-2 fw-bold">EmiGo</h3>
                <p className="text-muted">Gestión de Transporte Privado</p>
              </div>
              {error && <div className="alert alert-danger py-2 small">{error}</div>}
              <form onSubmit={handleSubmit}>
                <div className="form-group mb-3">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group mb-3">
                  <label>Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                  <div className="text-end mt-1">
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); setShowForgotModal(true); }}
                      className="text-muted small text-decoration-none"
                    >
                      ¿Olvidaste tu contraseña?
                    </a>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary w-100 mt-4" disabled={isLoading}>
                  {isLoading ? 'Logging in...' : 'Login'}
                </button>
              </form>
              <div className="text-center mt-3 text-muted" style={{ fontSize: '0.8rem' }}>
                Al iniciar sesión, aceptas nuestros{' '}
                <a
                  href="https://transportesemilia.ajisolutionsspa.cl/terminos-y-condiciones.html"
                  onClick={(e) => openPolicyLink(e, 'terminos-y-condiciones.html')}
                  className="text-decoration-none fw-semibold"
                >
                  Términos y Condiciones
                </a>{' '}
                y la{' '}
                <a
                  href="https://transportesemilia.ajisolutionsspa.cl/politica-privacidad.html"
                  onClick={(e) => openPolicyLink(e, 'politica-privacidad.html')}
                  className="text-decoration-none fw-semibold"
                >
                  Política de Privacidad
                </a>.
              </div>
              <div className="text-center mt-3 border-top pt-2">
                <p className="mb-0">
                  ¿Eres conductor? <Link to="/register-driver">Regístrate aquí</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Recuperación de Contraseña */}
      <Modal show={showForgotModal} onHide={() => setShowForgotModal(false)} centered>
        <Form onSubmit={handleForgotSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">Recuperar Contraseña</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small">
              Ingrese su correo electrónico registrado. Le enviaremos un enlace seguro para restablecer su clave de acceso.
            </p>
            <Form.Group>
              <Form.Label>Correo Electrónico</Form.Label>
              <Form.Control
                type="email"
                required
                placeholder="ejemplo@transportesemilia.cl"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                disabled={isForgotLoading}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowForgotModal(false)} disabled={isForgotLoading}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isForgotLoading}>
              {isForgotLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Enviando...
                </>
              ) : (
                'Enviar Enlace'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Login;